import React, { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { useUserSessionInit } from '../../hooks/useUserSessionInit'
import { CobuView, CobuText, CobuLoader } from '@cobu/ui'
import { MaintenanceScreen } from '../../components/MaintenanceScreen'
import { Strings } from '../../constants/Strings'

const SessionContext = createContext<any>(null)

export function useSession() {
  return useContext(SessionContext)
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const { isLoading, error, isErrorMessageSelfExplanatory, isMaintenanceMode } = useUserSessionInit()
  const auth = useSelector((s: RootState) => s.auth)
  const { buildingConfig } = useSelector((s: RootState) => s.settings)

  const session = useMemo(() => {
    if (!auth.isAuthenticated || !auth.token) return null
    return {
      ...auth,
      buildingConfig,
    }
  }, [auth, buildingConfig])

  if (isMaintenanceMode) {
    return <MaintenanceScreen />
  }

  const sessionStrings = Strings.get().sessionProvider

  if (error) {
    const [errorHeadline, ...errorBodyLines] = error.split('\n')
    const errorBody = errorBodyLines.join('\n').trim()

    return (
      <CobuView
        flex={1}
        jc="center"
        ai="center"
        bg="$background"
        padding="$6"
      >
        {isErrorMessageSelfExplanatory ? (
          <>
            <CobuText
              variant="subheading"
              color="$color"
              ta="center"
            >
              {errorHeadline}
            </CobuText>
            {!!errorBody && (
              <CobuText
                variant="body"
                color="$colorSecondary"
                ta="center"
                mt="$2"
              >
                {errorBody}
              </CobuText>
            )}
          </>
        ) : (
          <>
            <CobuText
              variant="subheading"
              color="$error"
              ta="center"
            >
              {sessionStrings.authError}
            </CobuText>
            <CobuText
              variant="body"
              color="$colorSecondary"
              ta="center"
              mt="$2"
            >
              {error}
            </CobuText>
          </>
        )}
      </CobuView>
    )
  }

  if (!session || isLoading) {
    return (
      <CobuView
        flex={1}
        jc="center"
        ai="center"
        bg="$background"
        minHeight="100vh"
      >
        <CobuLoader size="large" color="$cobuDarkBlue" />
        <CobuText
          variant="subheading"
          color="$cobuDarkBlue"
          ta="center"
          mt="$4"
        >
          {sessionStrings.initializing}
        </CobuText>
        <CobuText
          variant="body"
          color="$colorSecondary"
          ta="center"
          mt="$2"
        >
          {sessionStrings.pleaseWait}
        </CobuText>
      </CobuView>
    )
  }

  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>
}


